import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import updateCustomerPartNumber from "@midland-industries/Handlers/CustomerPartNumber/UpdateCustomerPartNumber";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        isAuthenticated: state.context.session.isAuthenticated,
    };
};

const mapDispatchToProps = {
    updateCustomerPartNumber,
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsCustomerPartNumberStyles {
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    editLink?: LinkPresentationProps;
    editInputGridItem?: GridItemProps;
    editButtonsGridItem?: GridItemProps;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export const customerPartNumberStyles: ProductDetailsCustomerPartNumberStyles = {
    labelText: {
        size: 16,
        weight: "bold",
        css: css`
            margin-right: 15px;
        `,
    },
    valueText: {
        size: 16,
        css: css`
            margin-bottom: 5px;
        `,
    },
    editLink: {},
    editInputGridItem: {
        width: [6, 6, 5, 4, 3],
    },
    editButtonsGridItem: {
        width: [6, 6, 5, 4, 3],
    },
    saveButton: {
        variant: "primary",
        css: css`
            margin-right: 1rem;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
};

const styles = customerPartNumberStyles;

const EditCustomerPartNumber: React.FC<Props> = ({ product, isAuthenticated, updateCustomerPartNumber }) => {
    const [newCustomerPartNumber, setNewCustomerPartNumber] = React.useState("");
    const [editCustomerPartNumber, setEditCustomerPartNumber] = React.useState(false);
    const handleSaveClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        updateCustomerPartNumber({
            customerPart: {
                customerPartNumber: newCustomerPartNumber,
                id: product.id,
                erpNumber: product.productNumber,
            },
            onComplete: () => {                
                setEditCustomerPartNumber(false);
            },
        });
    };

    if (!isAuthenticated) {
        return null;
    }

    return !editCustomerPartNumber ? (
        <Link
            {...styles.editLink}
            onClick={() => setEditCustomerPartNumber(true)}
            data-test-selector="customerPartNumber_edit"
        >
            {translate("Add/Edit your part number")}
        </Link>
    ) : (
        <GridContainer>
            <GridItem {...styles.editInputGridItem}>
                <TextField
                    name="newCustomerNumber"
                    value={newCustomerPartNumber}
                    onChange={e => setNewCustomerPartNumber(e.target.value)}
                    data-test-selector="customerPartNumber_input"
                />
            </GridItem>
            <GridItem {...styles.editButtonsGridItem}>
                <Button {...styles.saveButton} onClick={e => handleSaveClick(e)}>
                    {translate("Save")}
                </Button>
                <Button {...styles.cancelButton} onClick={() => setEditCustomerPartNumber(false)}>
                    {translate("Cancel")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(EditCustomerPartNumber)),
    definition: {
        displayName: "Edit Customer Part Number",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
